import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryRefetchInterval } from '@cast/shared-types';
import { PodMutatorStatus } from '@cast/types-patching-engine';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const POD_MUTATOR_STATUS_QUERY_KEY = 'autoscaler-pod-mutator-status';

const getPodMutatorStatusQueryKey = (clusterId?: string) =>
  [POD_MUTATOR_STATUS_QUERY_KEY, clusterId] as const;

const getPodMutatorStatusQueryOptions = ({
  organizationId,
  clusterId,
  refetchInterval,
}: {
  organizationId?: string;
  clusterId?: string;
  refetchInterval?: QueryRefetchInterval<PodMutatorStatus>;
}) =>
  queryOptions({
    queryKey: getPodMutatorStatusQueryKey(clusterId),
    queryFn: async () => {
      const { data } = await apiClient.autoscaler.getMutatorStatus({
        organizationId: organizationId!,
        clusterId: clusterId!,
      });
      return data;
    },
    enabled: !!clusterId && !!organizationId,
    refetchInterval,
  });

type UsePodMutatorStatusQueryArgs = {
  clusterId?: string;
  refetchInterval?: QueryRefetchInterval<PodMutatorStatus>;
};

type UsePodMutatorStatusQuery = {
  isLoading: boolean;
  data?: PodMutatorStatus;
  error: unknown;
  refetch?: () => void;
};

export const usePodMutatorStatusQuery = ({
  clusterId,
  refetchInterval,
}: UsePodMutatorStatusQueryArgs): UsePodMutatorStatusQuery => {
  const { currentOrganization } = useOrganizations();

  const { isLoading, data, error, refetch } = useQuery(
    getPodMutatorStatusQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      refetchInterval,
    })
  );

  return {
    isLoading,
    data,
    error,
    refetch,
  };
};
